import React, { useState, useEffect, useCallback } from "react";
import styled from "styled-components";
import { screenPanelStyles } from "../../ScreenPanel";
import { KEYS } from "../constants";
import { ChordProgression, ChordBarOption, CHORD_BAR_OPTIONS } from "../types";
import Selector from "./Selector";
import DropdownSelector from "./DropdownSelector";
import InteractiveChordProgression from "../../InteractiveChordProgression";
import ChordChanger from "../../ChordChanger";
import DroneKeyboard from "../../DroneKeyboard";
import ModePicker, { SequencerMode } from "./ModePicker";
import { noteNameToMidi } from "../../../utils/chordUtils";
import { midiToNote } from "../../../utils/romanParser";

interface ProgressionSectionProps {
  selectedKey: string;
  selectedMode: "minor" | "major";
  progression: ChordProgression;
  filteredProgressions: Record<string, ChordProgression>;
  samplesReady: boolean;
  onKeyChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  onModeChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  onProgressionChange: (
    e: React.ChangeEvent<HTMLSelectElement> | string
  ) => void;
  onChordUpdate?: (chordIndex: number, newChord: string) => void;
  onChordSwap?: (sourceIndex: number, targetIndex: number) => void;
  sequencerMode: SequencerMode;
  droneNotes: string[];
  onSequencerModeChange: (mode: SequencerMode) => void;
  onDroneNotesChange: (notes: string[]) => void;
  // Percentage-based ribbon positions
  ribbonPositions?: Array<{ startPercent: number; endPercent: number }>;
  onRibbonPositionsChange?: (
    positions: Array<{ startPercent: number; endPercent: number }>
  ) => void;
  // Chord bars (how many bars the chord sequence spans)
  chordBars?: ChordBarOption;
  onChordBarsChange?: (bars: ChordBarOption) => void;
  // Index of the currently playing chord (-1 = none)
  currentChordIndex?: number;
  onPreviewChord?: (roman: string) => void;
}

const ProgressionSection: React.FC<ProgressionSectionProps> = ({
  selectedKey,
  selectedMode,
  progression,
  filteredProgressions,
  samplesReady,
  onKeyChange,
  onModeChange,
  onProgressionChange,
  onChordUpdate,
  onChordSwap,
  sequencerMode,
  droneNotes,
  onSequencerModeChange,
  onDroneNotesChange,
  ribbonPositions,
  onRibbonPositionsChange,
  chordBars = 1,
  onChordBarsChange,
  currentChordIndex = -1,
  onPreviewChord,
}) => {
  // Get current progression name
  const currentProgressionName =
    Object.keys(filteredProgressions).find(
      (key) =>
        JSON.stringify(
          filteredProgressions[key as keyof typeof filteredProgressions]
        ) === JSON.stringify(progression)
    ) || "";

  // Get all progression keys for the current mode
  const progressionKeys = Object.keys(filteredProgressions);
  const currentIndex = progressionKeys.indexOf(currentProgressionName);

  // Handle previous progression
  const handlePreviousProgression = () => {
    if (!samplesReady) return;

    // Calculate the previous index (wrapping to the end if needed)
    const prevIndex =
      currentIndex > 0 ? currentIndex - 1 : progressionKeys.length - 1;
    onProgressionChange(progressionKeys[prevIndex]);
  };

  // Handle next progression
  const handleNextProgression = () => {
    if (!samplesReady) return;

    // Calculate the next index (wrapping to the beginning if needed)
    const nextIndex = (currentIndex + 1) % progressionKeys.length;
    onProgressionChange(progressionKeys[nextIndex]);
  };

  // Handle progression change from Selector
  const handleProgressionSelectorChange = (value: string) => {
    onProgressionChange(value);
  };

  // Chord changer state
  const [chordChangerOpen, setChordChangerOpen] = useState(false);
  const [selectedChordIndex, setSelectedChordIndex] = useState<number | null>(
    null
  );

  const stripOctave = (note: string): string => note.replace(/-?\d+$/, "");
  const notePitchClass = (note: string): number =>
    noteNameToMidi(stripOctave(note), 4) % 12;
  const notesMatchPitchClass = (note: string, other: string): boolean =>
    notePitchClass(note) === notePitchClass(other);

  // Initialize drone notes when mode switches to drone
  useEffect(() => {
    if (sequencerMode === "drone-mono" || sequencerMode === "drone-duo") {
      // Only initialize if drone notes are empty or don't match the mode
      const needsInitialization =
        droneNotes.length === 0 ||
        (sequencerMode === "drone-mono" && droneNotes.length !== 1) ||
        (sequencerMode === "drone-duo" && droneNotes.length !== 2);

      if (needsInitialization) {
        const rootKey = selectedKey.replace(/[mM]$/, "");
        const rootMidi = noteNameToMidi(rootKey, 4);
        const rootNote = stripOctave(midiToNote(rootMidi));

        if (sequencerMode === "drone-mono") {
          onDroneNotesChange([rootNote]);
        } else {
          // Duo mode: root and fifth
          const fifthMidi = rootMidi + 7; // Perfect fifth is 7 semitones
          const fifthNote = stripOctave(midiToNote(fifthMidi));
          onDroneNotesChange([rootNote, fifthNote]);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sequencerMode, selectedKey]);

  // Handle drone note click
  const handleDroneNoteClick = (note: string) => {
    const normalizedNote = stripOctave(note);
    if (sequencerMode === "drone-mono") {
      onDroneNotesChange([normalizedNote]);
    } else if (sequencerMode === "drone-duo") {
      // In duo mode, toggle selected notes. Require an unselect
      // before adding a new note when at the limit.
      const isSelected = droneNotes.some((existingNote) =>
        notesMatchPitchClass(existingNote, normalizedNote)
      );
      if (isSelected) {
        // If clicking a selected note, remove it (but keep at least one)
        if (droneNotes.length > 1) {
          onDroneNotesChange(
            droneNotes.filter(
              (existingNote) =>
                !notesMatchPitchClass(existingNote, normalizedNote)
            )
          );
        }
      } else {
        // Add the note only if there's space
        if (droneNotes.length < 2) {
          onDroneNotesChange([...droneNotes, normalizedNote]);
        }
      }
    }
  };

  // Handle chord click
  const handleChordClick = (chordIndex: number, _roman: string) => {
    setSelectedChordIndex(chordIndex);
    setChordChangerOpen(true);
  };

  // Handle chord change from ChordChanger
  const handleChordChange = (newChord: string) => {
    if (selectedChordIndex === null) return;

    // Update the progression's roman array
    if (onChordUpdate) {
      onChordUpdate(selectedChordIndex, newChord);
    }

    setChordChangerOpen(false);
    setSelectedChordIndex(null);
  };

  // Handle ribbon position changes from InteractiveChordProgression
  const handleRibbonChange = useCallback(
    (positions: Array<{ startPercent: number; endPercent: number }>) => {
      if (onRibbonPositionsChange) {
        onRibbonPositionsChange(positions);
      }
    },
    [onRibbonPositionsChange]
  );

  return (
    <ProgressionSectionContainer id="progression-section">
      <ProgressionSectionContent id="progression-section-content">
        <ProgressionControls id="progression-controls">
          {/* Left side: Key and Mode Dropdowns */}
          <DropdownsContainer id="dropdowns-container">
            <DropdownSelector
              id="key-dropdown"
              value={selectedKey}
              options={KEYS.map((key) => ({ key, label: key }))}
              onChange={onKeyChange}
              disabled={!samplesReady}
              minWidth="80px"
              showBorder={false}
            />
            <DropdownSelector
              id="mode-dropdown"
              value={selectedMode}
              options={[
                { key: "minor", label: "Minor" },
                { key: "major", label: "Major" },
              ]}
              onChange={onModeChange}
              disabled={!samplesReady}
              minWidth="100px"
              showBorder={false}
            />
          </DropdownsContainer>

          {/* Center: Progression Selector */}
          <ProgressionSelectorWrapper id="progression-selector-wrapper">
            <Selector
              id="progression-selector"
              value={currentProgressionName}
              options={Object.entries(filteredProgressions).map(
                ([key, prog]) => ({
                  key,
                  label: prog.roman.join(" - "),
                })
              )}
              onChange={handleProgressionSelectorChange}
              onPrevious={handlePreviousProgression}
              onNext={handleNextProgression}
              disabled={!samplesReady}
            />
          </ProgressionSelectorWrapper>

          {/* Right side: Chord Bars Selector or Drone Mode Selector */}
          <ChordBarsContainer id="chord-bars-container">
            <ChordBarsLabel>
              {sequencerMode === "chord" ? "Bars" : "Mode"}
            </ChordBarsLabel>
            <ChordBarsSelector id="chord-bars-selector">
              {sequencerMode === "chord" ? (
                // Bars selector for chord mode
                CHORD_BAR_OPTIONS.map((bar) => (
                  <ChordBarsButton
                    key={bar}
                    $active={chordBars === bar}
                    $disabled={!samplesReady}
                    onClick={() => samplesReady && onChordBarsChange?.(bar)}
                  >
                    {bar}
                  </ChordBarsButton>
                ))
              ) : (
                // Mono/Duo selector for drone mode
                <>
                  <ChordBarsButton
                    key="mono"
                    $active={sequencerMode === "drone-mono"}
                    $disabled={!samplesReady}
                    onClick={() =>
                      samplesReady && onSequencerModeChange("drone-mono")
                    }
                  >
                    Mono
                  </ChordBarsButton>
                  <ChordBarsButton
                    key="duo"
                    $active={sequencerMode === "drone-duo"}
                    $disabled={!samplesReady}
                    onClick={() =>
                      samplesReady && onSequencerModeChange("drone-duo")
                    }
                  >
                    Duo
                  </ChordBarsButton>
                </>
              )}
            </ChordBarsSelector>
          </ChordBarsContainer>
        </ProgressionControls>

        {/* Interactive Chord Progression or Drone Keyboard */}
        {sequencerMode === "chord" && (
          <InteractiveChordProgressionWrapper id="interactive-chord-progression-wrapper">
            <InteractiveChordProgression
              progression={progression}
              selectedKey={selectedKey}
              onProgressionChange={handleRibbonChange}
              disabled={!samplesReady}
              onChordClick={handleChordClick}
              onChordSwap={onChordSwap}
              ribbonPositions={ribbonPositions}
              currentChordIndex={currentChordIndex}
              barCount={chordBars}
            />
          </InteractiveChordProgressionWrapper>
        )}

        {(sequencerMode === "drone-mono" || sequencerMode === "drone-duo") && (
          <DroneKeyboardWrapper id="drone-keyboard-wrapper">
            <DroneKeyboard
              selectedKey={selectedKey}
              selectedMode={selectedMode}
              droneNotes={droneNotes}
              droneMode={sequencerMode === "drone-mono" ? "mono" : "duo"}
              disabled={!samplesReady}
              onNoteClick={handleDroneNoteClick}
            />
          </DroneKeyboardWrapper>
        )}

        {/* Mode Picker */}
        <ModePickerWrapper id="mode-picker-wrapper">
          <ModePicker
            mode={sequencerMode}
            disabled={!samplesReady}
            onModeChange={onSequencerModeChange}
          />
        </ModePickerWrapper>
      </ProgressionSectionContent>
      {/* Chord Changer Modal */}
      {chordChangerOpen && selectedChordIndex !== null && (
        <ChordChanger
          isOpen={chordChangerOpen}
          onClose={() => {
            setChordChangerOpen(false);
            setSelectedChordIndex(null);
          }}
          currentChord={progression.roman[selectedChordIndex]}
          currentKey={selectedKey}
          mode={selectedMode}
          onChordChange={handleChordChange}
          onPreviewChord={onPreviewChord}
        />
      )}
    </ProgressionSectionContainer>
  );
};

export default ProgressionSection;

// Styled Components
const ProgressionSectionContainer = styled.div`
  ${screenPanelStyles}
  width: 100%;
  margin-top: 16px;
  box-sizing: border-box;
  padding: 8px 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  position: relative;
`;

const ProgressionSectionContent = styled.div.attrs({
  id: "progression-section-content-wrapper",
})`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ProgressionControls = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 12px;
  width: 100%;
`;

const DropdownsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  justify-self: start;
`;

const ProgressionSelectorWrapper = styled.div`
  display: flex;
  justify-content: center;
  justify-self: center;
  min-width: 200px;
`;

const InteractiveChordProgressionWrapper = styled.div`
  width: 100%;
  height: auto;
`;

const DroneKeyboardWrapper = styled.div`
  width: 100%;
`;

const ModePickerWrapper = styled.div`
  width: 100%;
`;

const ChordBarsContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  justify-self: end;
`;

const ChordBarsLabel = styled.span`
  font-family: "Dogica Pixel", monospace;
  font-size: 8px;
  color: #cfc9c9;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  image-rendering: pixelated;
`;

const ChordBarsSelector = styled.div`
  display: flex;
  align-items: stretch;
  border: 2px solid #cfc9c9;
  border-radius: 4px;
  overflow: hidden;
`;

const ChordBarsButton = styled.button<{ $active: boolean; $disabled: boolean }>`
  background-color: ${(props) => (props.$active ? "#CFC9C9" : "transparent")};
  border: none;
  border-right: 2px solid #cfc9c9;
  color: ${(props) => (props.$active ? "#000" : "#CFC9C9")};
  font-family: "Dogica Pixel", monospace;
  font-size: 10px;
  padding: 6px 10px;
  cursor: ${(props) => (props.$disabled ? "not-allowed" : "pointer")};
  opacity: ${(props) => (props.$disabled ? 0.5 : 1)};
  user-select: none;
  image-rendering: pixelated;
  transition: all 0.15s ease;

  &:last-child {
    border-right: none;
  }

  &:hover {
    ${(props) =>
      !props.$disabled &&
      !props.$active &&
      `
      background-color: rgba(207, 201, 201, 0.1);
    `}
  }

  &:active {
    ${(props) => !props.$disabled && "opacity: 0.8;"}
  }
`;
